import { useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  VerifiedUser as VerifiedIcon,
  PersonOff as UnverifiedIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../hooks/useAuth';
import { useUsers } from '../hooks/useAdmin';
import { useAvailablePlans } from '../hooks/useCommon';
import PageSection from '../components/PageSection';
import { hexToRgba } from '../user/content/utils/colorUtils';

export default function AdminDashboard() {
  const { user } = useAuth();
  // All hooks must be called before any conditional returns
  const { data: usersData, isLoading, error } = useUsers();
  const { data: plansData } = useAvailablePlans();

  // Check if user is admin (after hooks)
  if (user?.role !== 'admin') {
    return (
      <PageSection title="Admin Dashboard" hideTitleOnMobile>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </PageSection>
    );
  }

  // Handle API response structure
  const allUsers = useMemo(() => {
    if (!usersData) return [];
    if (typeof usersData === 'object' && 'users' in usersData) {
      return (usersData as any).users || [];
    }
    return Array.isArray(usersData) ? usersData : [];
  }, [usersData]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalUsers = allUsers.length;
    const byRole = {
      client: allUsers.filter((u: any) => u.role === 'client').length,
      admin: allUsers.filter((u: any) => u.role === 'admin').length,
      agency: allUsers.filter((u: any) => u.role === 'agency').length,
    };
    const byStatus = {
      active: allUsers.filter((u: any) => u.is_active).length,
      inactive: allUsers.filter((u: any) => !u.is_active).length,
    };
    const byVerification = {
      verified: allUsers.filter((u: any) => u.is_verified).length,
      unverified: allUsers.filter((u: any) => !u.is_verified).length,
    };

    // Calculate by plan
    const planMap: Record<number, number> = {};
    allUsers.forEach((u: any) => {
      const planId = u.plan_id || 0;
      planMap[planId] = (planMap[planId] || 0) + 1;
    });

    // Create plan distribution data
    const planDistribution = plansData && Array.isArray(plansData)
      ? plansData.map((plan: any) => ({
          name: plan.name || `Plan ${plan.id}`,
          count: planMap[plan.id] || 0,
        }))
      : [];

    return {
      totalUsers,
      byRole,
      byStatus,
      byVerification,
      planDistribution,
    };
  }, [allUsers, plansData]);

  // Chart data - ensure we have valid data
  const roleChartData = useMemo(() => [
    { name: 'Clients', value: stats.byRole.client, color: '#6C63FF' },
    { name: 'Admins', value: stats.byRole.admin, color: '#ef4444' },
    { name: 'Agencies', value: stats.byRole.agency, color: '#10b981' },
  ].filter(item => item.value > 0), [stats.byRole]);

  const statusChartData = useMemo(() => [
    { name: 'Active', value: stats.byStatus.active, color: '#10b981' },
    { name: 'Inactive', value: stats.byStatus.inactive, color: '#9ca3af' },
  ].filter(item => item.value > 0), [stats.byStatus]);

  const verificationChartData = useMemo(() => [
    { name: 'Verified', value: stats.byVerification.verified, color: '#10b981' },
    { name: 'Unverified', value: stats.byVerification.unverified, color: '#f59e0b' },
  ].filter(item => item.value > 0), [stats.byVerification]);

  // Custom label for pie charts
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={14}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (isLoading) {
    return (
      <PageSection title="Admin Dashboard" subtitle="Platform insights and analytics" hideTitleOnMobile>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection title="Admin Dashboard" subtitle="Platform insights and analytics" hideTitleOnMobile>
        <Alert severity="error">
          Failed to load dashboard data. Please try again later.
        </Alert>
      </PageSection>
    );
  }

  return (
    <PageSection
      title="Admin Dashboard"
      subtitle="Platform insights and analytics"
      hideTitleOnMobile
    >
      {/* Stats Cards Row 1 - Total Users and Roles */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #6C63FF 0%, #FF6CAB 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(108, 99, 255, 0.3)',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stats.totalUsers}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Users
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#6C63FF' }}>
                    {stats.byRole.client}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clients
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: '#6C63FF' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#ef4444' }}>
                    {stats.byRole.admin}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Admins
                  </Typography>
                </Box>
                <AdminIcon sx={{ fontSize: 40, color: '#ef4444' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#10b981' }}>
                    {stats.byRole.agency}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Agencies
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: 40, color: '#10b981' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stats Cards Row 2 - Status and Verification */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: hexToRgba('#10b981', 0.05),
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#10b981' }}>
                    {stats.byStatus.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Accounts
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#10b981' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: hexToRgba('#9ca3af', 0.05),
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#9ca3af' }}>
                    {stats.byStatus.inactive}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Accounts
                  </Typography>
                </Box>
                <CancelIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: hexToRgba('#10b981', 0.05),
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#10b981' }}>
                    {stats.byVerification.verified}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verified
                  </Typography>
                </Box>
                <VerifiedIcon sx={{ fontSize: 40, color: '#10b981' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: hexToRgba('#f59e0b', 0.05),
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#f59e0b' }}>
                    {stats.byVerification.unverified}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unverified
                  </Typography>
                </Box>
                <UnverifiedIcon sx={{ fontSize: 40, color: '#f59e0b' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Role Distribution Pie Chart */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Role Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={roleChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {roleChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Status Pie Chart */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Account Status
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Verification Status Pie Chart */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Verification Status
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={verificationChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {verificationChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Plan Distribution Bar Chart */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card sx={{ 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Users by Plan
              </Typography>
              {stats.planDistribution && stats.planDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={stats.planDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar 
                      dataKey="count" 
                      fill="#6C63FF"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Typography color="text.secondary">No plan data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageSection>
  );
}

